import { z } from "zod";
import { SET_KINDS } from "./workout";
/**
 * Training history from another app — `POST /me/imports` — and out of this
 * one — `GET /me/export.csv` (`D201`).
 *
 * ── THE DEVICE PARSES, THE SERVER WRITES HISTORY ───────────────────────────
 * Strong and Hevy export CSV, and matching their exercise names to this
 * catalogue is a judgement the person has to see and correct, so it happens
 * on the device before anything is sent. What arrives here is already this
 * catalogue's ids, kilograms and metres, batched — sessions that ENDED, with
 * no plan, which never advance one and promise no follow-ups (`D116` does not
 * apply: nothing ended in this app).
 *
 * ── A RESEND IS THE SAME HISTORY ───────────────────────────────────────────
 * `importId` is the client's key for one source session — its app, its start
 * and its title, as the device read them — and the server derives the session
 * id from it and the account. The same file imported twice, or a batch resent
 * after a lost answer, writes nothing the second time: the session already
 * exists and is answered `created: false`. A session the person deleted after
 * importing stays deleted.
 */
export const IMPORT_SOURCES = ["strong", "hevy"];
export const importSource = z.enum(IMPORT_SOURCES);
/** The set kinds an import may carry (`0028`, `D209`); absent or `null` is a
 *  working set. Hevy's `warmup` / `dropset` / `failure` and Strong's `W` /
 *  `D` / `F` map onto them on the device. The SAME tuple as a logged set's
 *  `SET_KINDS`, not a copy: an imported warm-up has to be the word every
 *  figure leaves out. */
export const IMPORT_SET_KINDS = SET_KINDS;
/** Per request. A request is one D1 batch: 30 sessions and 600 sets is ~150
 *  statements, far under the per-invocation ceiling, and an import of years
 *  is a loop of these on the device. */
export const MAX_IMPORT_SESSIONS = 30;
export const MAX_IMPORT_SETS = 600;
export const MAX_IMPORT_ID_LENGTH = 200;
/** The same bounds `WorkoutSet` holds, so a refusal here is a 422 a screen can
 *  render rather than the aggregate's later one. */
const importedSet = z.strictObject({
    distanceMetres: z.number().positive().max(1_000_000).nullable().optional(),
    durationSeconds: z.int().positive().max(86_400).nullable().optional(),
    exerciseId: z.string().min(1).max(64),
    kind: z.enum(IMPORT_SET_KINDS).nullable().optional(),
    load: z.number().positive().max(2000).nullable().optional(),
    /** Omitted: the session's start plus one second per set, so the order the
     *  source wrote them in is the order every history read returns. */
    performedAt: z.iso.datetime().optional(),
    reps: z.int().positive().max(10_000).nullable().optional(),
    rpe: z.number().positive().max(10).nullable().optional(),
});
const importedSession = z.strictObject({
    endedAt: z.iso.datetime(),
    importId: z.string().min(1).max(MAX_IMPORT_ID_LENGTH),
    sets: z.array(importedSet).min(1).max(MAX_IMPORT_SETS),
    startedAt: z.iso.datetime(),
});
export const importBatchInput = z
    .strictObject({
    sessions: z.array(importedSession).min(1).max(MAX_IMPORT_SESSIONS),
    source: importSource,
})
    .refine((batch) => batch.sessions.reduce((sum, one) => sum + one.sets.length, 0) <=
    MAX_IMPORT_SETS, {
    message: `a batch carries at most ${MAX_IMPORT_SETS} sets`,
    path: ["sessions"],
});
export const importBatchOutput = z.object({
    /** One line per session sent, in the order sent. */
    sessions: z.array(z.object({
        /** `false`: it was already here — imported before, or this is a resend. */
        created: z.boolean(),
        importId: z.string(),
        sessionId: z.string(),
    })),
});
/**
 * `GET /me/export.csv?locale` — every ended session's sets, Strong's columns,
 * streamed. Exercise names in the catalogue language asked for, English by
 * default (what Strong and Hevy match against).
 */
export const exportQuery = z.object({
    locale: z.enum(["en", "pt"]).optional(),
});
/** Strong's header, in Strong's order — the file is one it can read back. */
export const EXPORT_CSV_COLUMNS = [
    "Date",
    "Workout Name",
    "Duration",
    "Exercise Name",
    "Set Order",
    "Weight",
    "Reps",
    "Distance",
    "Seconds",
    "Notes",
    "Workout Notes",
    "RPE",
];
//# sourceMappingURL=imports.js.map